/**
 * ITT-972: beer profile processing failure queue helpers for Engage UI.
 * Sibling to Inc 1 beerIngestionFailures — do not reuse those categories.
 */

export const BEER_PROFILE_PROCESSING_FAILURE_CATEGORIES = [
  'retain_failed',
  'shadow_rebuild_failed',
  'legacy_seed_failed',
  'legacy_shadow_rebuild_failed',
];

export const BEER_PROFILE_PROCESSING_FAILURE_STAGES = [
  'observation_retain',
  'shadow_rebuild',
  'legacy_seed',
];

export const BEER_PROFILE_PROCESSING_FAILURE_CATEGORY_LABELS = {
  retain_failed: 'Observation retain failed',
  shadow_rebuild_failed: 'Shadow rebuild failed',
  legacy_seed_failed: 'Legacy seed failed',
  legacy_shadow_rebuild_failed: 'Legacy shadow rebuild failed',
};

export const BEER_PROFILE_PROCESSING_FAILURE_STAGE_LABELS = {
  observation_retain: 'Observation retain',
  shadow_rebuild: 'Shadow rebuild',
  legacy_seed: 'Legacy seed',
};

export const BEER_PROFILE_PROCESSING_FAILURE_RETRY_LABELS = {
  retain_failed: 'Retry retain',
  shadow_rebuild_failed: 'Rebuild shadow',
  legacy_seed_failed: 'Retry seed',
  legacy_shadow_rebuild_failed: 'Rebuild shadow',
};

export const PROFILE_PROCESSING_FILTER_ALL = 'all';

/** Soft 5-minute visibility SLA (AC6) — matches backend reportingStale. */
export const PROFILE_PROCESSING_REPORTING_SLA_MS = 5 * 60 * 1000;

export function formatBeerProfileProcessingFailureCategoryLabel(category) {
  return BEER_PROFILE_PROCESSING_FAILURE_CATEGORY_LABELS[category]
    || category
    || 'Unknown';
}

export function formatBeerProfileProcessingFailureStageLabel(stage) {
  return BEER_PROFILE_PROCESSING_FAILURE_STAGE_LABELS[stage] || stage || 'Unknown';
}

export function primaryRetryLabelForProfileFailureCategory(category) {
  return BEER_PROFILE_PROCESSING_FAILURE_RETRY_LABELS[category] || 'Retry';
}

export function isProfileFailureRetryDisabled(failureItem) {
  if (!failureItem || failureItem.disposition === 'resolved') {
    return true;
  }
  return failureItem.retryState === 'in_progress';
}

export function truncateProfileFailureError(message, maxLength = 120) {
  const trimmed = typeof message === 'string' ? message.trim() : '';
  if (!trimmed) {
    return '—';
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

export function profileFailureRetryHelperCopy(category) {
  if (category === 'retain_failed') {
    return 'Retry retain re-enters observation createIfAbsent for the run/candidate; it does not re-apply or mutate live beer.';
  }
  if (category === 'shadow_rebuild_failed' || category === 'legacy_shadow_rebuild_failed') {
    return 'Rebuild shadow re-runs profile resolve + live compare; live beer fields are never written.';
  }
  if (category === 'legacy_seed_failed') {
    return 'Retry seed re-runs legacy observation seed for the beer; it does not mutate live beer profile fields.';
  }
  return 'Retry re-enters the failed 1a stage without mutating live beer.';
}

/**
 * Count resolved items that recorded a retry (retry outcomes for pulse).
 *
 * @param {Array<{ disposition?: string, retriedAt?: unknown, retryState?: string }>|null|undefined} rows
 * @returns {number}
 */
export function countProfileFailureRetryOutcomes(rows) {
  if (!Array.isArray(rows)) {
    return 0;
  }
  return rows.filter((row) => {
    if (!row) {
      return false;
    }
    if (row.retriedAt) {
      return true;
    }
    return row.retryState === 'succeeded' || row.retryState === 'failed';
  }).length;
}

/**
 * Build NH pilot pulse metrics from failure summary + shadow version counts.
 * Conflicts and open failures are incomplete — never counted as success (AC4).
 *
 * @param {{
 *   beerCount?: number,
 *   shadowCount?: number,
 *   proposedDiffCount?: number,
 *   conflictCount?: number,
 *   openFailures?: number,
 *   resolvedFailures?: number,
 *   retryOutcomes?: number,
 *   reportingAsOf?: Date | string | null,
 *   reportingStale?: boolean,
 *   byStage?: Record<string, number>,
 * }} input
 */
export function buildProfileProcessingPulse(input = {}) {
  const beerCount = Number(input.beerCount) || 0;
  const shadowCount = Number(input.shadowCount) || 0;
  const proposedDiffCount = Number(input.proposedDiffCount) || 0;
  const conflictCount = Number(input.conflictCount) || 0;
  const openFailures = Number(input.openFailures) || 0;
  const resolvedFailures = Number(input.resolvedFailures) || 0;
  const retryOutcomes = Number(input.retryOutcomes) || 0;
  const coveragePercent = beerCount > 0
    ? Math.round((shadowCount / beerCount) * 100)
    : 0;
  // Honest completion: shadow present, not conflicted. Open failures stay incomplete.
  const completeWithoutConflict = Math.max(0, shadowCount - conflictCount);
  const completionPercent = beerCount > 0
    ? Math.round((completeWithoutConflict / beerCount) * 100)
    : 0;
  const incompleteCount = conflictCount + openFailures;

  return {
    beerCount,
    shadowCount,
    proposedDiffCount,
    conflictCount,
    openFailures,
    resolvedFailures,
    retryOutcomes,
    coveragePercent,
    completionPercent,
    incompleteCount,
    reportingAsOf: input.reportingAsOf ?? null,
    reportingStale: input.reportingStale === true,
    byStage:
      input.byStage && typeof input.byStage === 'object'
        ? input.byStage
        : {},
  };
}
